/**
 * The three numbers that describe a project's money, budget against actual.
 *
 * Total CAPEX     - Acquisition + Development costs (what it costs to own and build)
 * Operating income - Operation revenue less operating expenses (the rental NOI)
 * Closing income   - the net Exit result less Total CAPEX (what the project ultimately makes)
 *
 * Shared by the project Financials tab and the Finance page so both always agree.
 */
import React from 'react';
import { Card } from '../../ui/card';
import { Buildings, House, Handshake, ArrowUp, ArrowDown, Minus } from '@phosphor-icons/react';
import { cn } from '../../../lib/utils';
import { phasesForProjectType } from '../../../lib/projectPhases';

// Negative money reads as -€250,250, never €-250,250
const money = (v) => `${v < 0 ? '-' : ''}€${Math.round(Math.abs(v || 0)).toLocaleString()}`;

const KPI_CONFIG = [
  {
    key: 'capex',
    label: 'Total CAPEX',
    hint: 'Acquisition + Development',
    icon: Buildings,
    chip: 'bg-sky-100 text-sky-700',
    // Spending more than planned is bad
    higherIsBetter: false,
  },
  {
    key: 'operating_income',
    label: 'Operating income',
    hint: 'Operation revenue − expenses',
    icon: House,
    chip: 'bg-emerald-100 text-emerald-700',
    higherIsBetter: true,
  },
  {
    key: 'closing_income',
    label: 'Closing income',
    hint: 'Exit result − Total CAPEX',
    icon: Handshake,
    chip: 'bg-violet-100 text-violet-700',
    higherIsBetter: true,
  },
];

const VarianceTag = ({ budget, actual, higherIsBetter }) => {
  const diff = (actual || 0) - (budget || 0);
  const pct = budget ? (diff / Math.abs(budget)) * 100 : null;

  if (!budget && !actual) {
    return <span className="text-xs text-foreground-muted">Nothing recorded</span>;
  }
  if (!diff) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-blue-600">
        <Minus className="h-3 w-3" /> On budget
      </span>
    );
  }
  const good = higherIsBetter ? diff > 0 : diff < 0;
  const Icon = diff > 0 ? ArrowUp : ArrowDown;
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs', good ? 'text-green-600' : 'text-red-600')}>
      <Icon className="h-3 w-3" weight="bold" />
      {money(Math.abs(diff))}
      {pct !== null && ` (${diff > 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)}%)`}
    </span>
  );
};

// Both returns are read against Total CAPEX - what the project cost to build and own
const percentLabel = {
  operating_income: 'NOI on CAPEX',
  closing_income: 'profit on CAPEX',
};

export const FinanceKpiBar = ({ kpis, className, projectType }) => {
  // A flip has no Operation phase, so an Operating income card would only ever read zero
  const phases = projectType ? phasesForProjectType(projectType) : null;
  const cards = KPI_CONFIG.filter((card) => {
    if (!phases) return true;
    if (card.key === 'operating_income') return phases.includes('operation');
    if (card.key === 'closing_income') return phases.includes('exit');
    return true;
  });

  return (
  <div className={cn('grid grid-cols-1 gap-4', cards.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2', className)} data-testid="finance-kpi-bar">
    {cards.map(({ key, label, hint, icon: Icon, chip, higherIsBetter }) => {
      const data = kpis?.[key] || {};
      return (
        <Card
          key={key}
          className="bg-white p-4 rounded-xl border border-black/[0.04]"
          data-testid={`finance-kpi-${key}`}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className={cn('p-1.5 rounded-lg', chip)}>
              <Icon className="h-4 w-4" weight="duotone" />
            </span>
            <div>
              <p className="text-sm font-medium leading-tight">{label}</p>
              <p className="text-[11px] text-foreground-muted">{hint}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-foreground-muted">Budget</p>
              <p className="text-lg font-bold tabular-nums" data-testid={`finance-kpi-${key}-budget`}>
                {money(data.budget)}
              </p>
              {data.percent?.budget !== null && data.percent?.budget !== undefined && (
                <p className="text-[11px] text-foreground-muted tabular-nums" data-testid={`finance-kpi-${key}-budget-percent`}>
                  {data.percent.budget.toFixed(1)}% {percentLabel[key]}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-foreground-muted">Actual</p>
              <p className="text-lg font-bold tabular-nums" data-testid={`finance-kpi-${key}-actual`}>
                {money(data.actual)}
              </p>
              {data.percent?.actual !== null && data.percent?.actual !== undefined && (
                <p
                  className={cn('text-[11px] tabular-nums', data.percent.actual < 0 ? 'text-red-600' : 'text-foreground-muted')}
                  data-testid={`finance-kpi-${key}-actual-percent`}
                >
                  {data.percent.actual.toFixed(1)}% {percentLabel[key]}
                </p>
              )}
            </div>
          </div>

          <div className="mt-2 pt-2 border-t border-black/[0.04]">
            <VarianceTag budget={data.budget} actual={data.actual} higherIsBetter={higherIsBetter} />
          </div>
        </Card>
      );
    })}
  </div>
  );
};

export default FinanceKpiBar;
